/**
 * Fitting jobs for fitters (plan FE9): where a fitter works, the jobs in those
 * places, and the quotes they send.
 *
 * <p>A port. The live adapter (`liveTradeJobs.ts`) reads the signed-in fitter's
 * account (the profile's `installer`, at `/api/storefront/account/me`) and the
 * jobs the shop's staff opened, and sends quotes, through the storefront's
 * `/api/storefront/installers/me/jobs/**` routes (contract:
 * `commerce/docs/TRADE_AND_INSTALLER_JOBS_CONTRACT.md` §5). The server decides
 * which jobs a fitter sees; the theme never filters jobs itself.
 *
 * <h3>Privacy</h3>
 * Before an award a job carries only its outward code ("BS16") and the area's
 * name, never a full postcode, an address or a customer's name. Only the fitter
 * whose quote was accepted gets the site postcode ({@link TradeJob.sitePostcode}),
 * and the shop, not the customer, arranges the survey.
 */

// ---------------------------------------------------------------------------
// Postcodes
// ---------------------------------------------------------------------------

/**
 * Every Royal Mail postcode area, with the name Royal Mail gives it. Guernsey,
 * Jersey and the Isle of Man (GY, JE, IM) use the same scheme and are included
 * so a fitter there can say so; whether the shop serves them is a business
 * decision made elsewhere, not a postcode rule.
 */
export const POSTCODE_AREAS: Readonly<Record<string, string>> = {
  AB: 'Aberdeen', AL: 'St Albans', B: 'Birmingham', BA: 'Bath', BB: 'Blackburn', BD: 'Bradford',
  BH: 'Bournemouth', BL: 'Bolton', BN: 'Brighton', BR: 'Bromley', BS: 'Bristol', BT: 'Belfast',
  CA: 'Carlisle', CB: 'Cambridge', CF: 'Cardiff', CH: 'Chester', CM: 'Chelmsford', CO: 'Colchester',
  CR: 'Croydon', CT: 'Canterbury', CV: 'Coventry', CW: 'Crewe', DA: 'Dartford', DD: 'Dundee',
  DE: 'Derby', DG: 'Dumfries', DH: 'Durham', DL: 'Darlington', DN: 'Doncaster', DT: 'Dorchester',
  DY: 'Dudley', E: 'East London', EC: 'East Central London', EH: 'Edinburgh', EN: 'Enfield',
  EX: 'Exeter', FK: 'Falkirk', FY: 'Blackpool', G: 'Glasgow', GL: 'Gloucester', GU: 'Guildford',
  GY: 'Guernsey', HA: 'Harrow', HD: 'Huddersfield', HG: 'Harrogate', HP: 'Hemel Hempstead',
  HR: 'Hereford', HS: 'Outer Hebrides', HU: 'Hull', HX: 'Halifax', IG: 'Ilford', IM: 'Isle of Man',
  IP: 'Ipswich', IV: 'Inverness', JE: 'Jersey', KA: 'Kilmarnock', KT: 'Kingston upon Thames',
  KW: 'Kirkwall', KY: 'Kirkcaldy', L: 'Liverpool', LA: 'Lancaster', LD: 'Llandrindod Wells',
  LE: 'Leicester', LL: 'Llandudno', LN: 'Lincoln', LS: 'Leeds', LU: 'Luton', M: 'Manchester',
  ME: 'Medway', MK: 'Milton Keynes', ML: 'Motherwell', N: 'North London', NE: 'Newcastle upon Tyne',
  NG: 'Nottingham', NN: 'Northampton', NP: 'Newport', NR: 'Norwich', NW: 'North West London',
  OL: 'Oldham', OX: 'Oxford', PA: 'Paisley', PE: 'Peterborough', PH: 'Perth', PL: 'Plymouth',
  PO: 'Portsmouth', PR: 'Preston', RG: 'Reading', RH: 'Redhill', RM: 'Romford', S: 'Sheffield',
  SA: 'Swansea', SE: 'South East London', SG: 'Stevenage', SK: 'Stockport', SL: 'Slough',
  SM: 'Sutton', SN: 'Swindon', SO: 'Southampton', SP: 'Salisbury', SR: 'Sunderland',
  SS: 'Southend-on-Sea', ST: 'Stoke-on-Trent', SW: 'South West London', SY: 'Shrewsbury',
  TA: 'Taunton', TD: 'Galashiels', TF: 'Telford', TN: 'Tonbridge', TQ: 'Torquay', TR: 'Truro',
  TS: 'Teesside', TW: 'Twickenham', UB: 'Southall', W: 'West London', WA: 'Warrington',
  WC: 'West Central London', WD: 'Watford', WF: 'Wakefield', WN: 'Wigan', WR: 'Worcester',
  WS: 'Walsall', WV: 'Wolverhampton', YO: 'York', ZE: 'Shetland',
};

/**
 * Only central London districts are split into sub-districts with a trailing
 * letter (W1A, EC1A, SW1A, N1C, E1W, NW1W, SE1P, WC2H). "BS1A" is not a code.
 */
const SUB_DISTRICT_AREAS: ReadonlySet<string> = new Set(['E', 'EC', 'N', 'NW', 'SE', 'SW', 'W', 'WC']);

export type PostcodeKind = 'area' | 'district';

export type PostcodeCheck =
  | { ok: true; code: string; kind: PostcodeKind }
  | { ok: false; reason: string };

export const FULL_POSTCODE_MESSAGE = 'Just the first part, e.g. BS16';

/**
 * Checks one postcode AREA ("BS") or outward code, i.e. district ("BS16",
 * "SW1A"), and returns it in its canonical form: upper case, no spaces.
 *
 * <p>Rules, decided here and asserted in `test/tradeJobs.test.ts`:
 * <ul>
 *   <li>The area must be a real one ({@link POSTCODE_AREAS}), so "QQ1" is refused.</li>
 *   <li>A district is the area plus one or two digits ("B1", "BS16", "BS0"); a
 *       two-digit district never starts with 0 ("BS01" is refused).</li>
 *   <li>A trailing letter is accepted only in central London ({@link SUB_DISTRICT_AREAS}):
 *       "W1A", "EC1A", "SW1A" pass, "BS1A" does not.</li>
 *   <li>A full postcode ("BS16 1AB", "BS161AB") or a sector ("BS16 1") is refused
 *       with {@link FULL_POSTCODE_MESSAGE}: a fitter covers districts, and a full
 *       postcode is someone's street.</li>
 *   <li>GIR 0AA (the old Girobank code) and BFPO numbers are not geographic and
 *       are refused like any other unknown code.</li>
 * </ul>
 */
export function checkPostcode(input: string): PostcodeCheck {
  const raw = input.trim().toUpperCase();
  if (!raw) return { ok: false, reason: 'Type a postcode area or district, e.g. BS or BS16.' };
  const compact = raw.replace(/\s+/g, '');
  const hasInward = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(compact) || /^[A-Z]{1,2}\d[A-Z\d]?\s+\d[A-Z]{0,2}$/.test(raw);
  if (hasInward) return { ok: false, reason: FULL_POSTCODE_MESSAGE };
  const match = /^([A-Z]{1,2})(\d{1,2})?([A-Z])?$/.exec(compact);
  const area = match?.[1];
  if (!match || !area || !(area in POSTCODE_AREAS)) {
    return { ok: false, reason: `${compact} is not a UK postcode area or district. Try one like BS or BS16.` };
  }
  const digits = match[2];
  const letter = match[3];
  if (!digits) {
    if (letter) return { ok: false, reason: `${compact} is not a UK postcode area or district. Try one like BS or BS16.` };
    return { ok: true, code: area, kind: 'area' };
  }
  if (digits.length === 2 && digits.startsWith('0')) {
    return { ok: false, reason: `${compact} is not a UK postcode district. Try ${area}${digits.slice(1)}.` };
  }
  if (letter && !SUB_DISTRICT_AREAS.has(area)) {
    return { ok: false, reason: `${compact} is not a UK postcode district. Try ${area}${digits}.` };
  }
  return { ok: true, code: `${area}${digits}${letter ?? ''}`, kind: 'district' };
}

/** The canonical form of a valid area or district, or `null`. */
export function normalisePostcode(input: string): string | null {
  const check = checkPostcode(input);
  return check.ok ? check.code : null;
}

/** The area letters of a district or area: "BS16" → "BS", "W1A" → "W". */
export function postcodeArea(code: string): string {
  return /^[A-Z]{1,2}/.exec(code.toUpperCase())?.[0] ?? '';
}

/** The area's name, e.g. "Bristol", or `''` for an unknown area. */
export function areaName(code: string): string {
  return POSTCODE_AREAS[postcodeArea(code)] ?? '';
}

/**
 * Whether a coverage entry takes in a job's outward code:
 * <ul>
 *   <li>the same district ("BS16" covers "BS16");</li>
 *   <li>its whole area ("BS" covers "BS16", but "B" does NOT cover "BS16": the area is compared whole);</li>
 *   <li>a central London district takes in its sub-districts ("W1" covers "W1A"; "W1" does not cover "W10").</li>
 * </ul>
 */
export function covers(entry: string, outward: string): boolean {
  const c = entry.toUpperCase();
  const j = outward.toUpperCase();
  if (c === j) return true;
  if (/^[A-Z]{1,2}$/.test(c)) return postcodeArea(j) === c;
  return j.length === c.length + 1 && j.startsWith(c) && /[A-Z]$/.test(j);
}

export function isCovered(postcodes: readonly string[], outward: string): boolean {
  return postcodes.some((entry) => covers(entry, outward));
}

export interface AddPostcodesResult {
  postcodes: string[];
  added: string[];
  /** Already in the list, or already taken in by an area in it. */
  skipped: string[];
  /** Districts dropped because an area just added takes them in. */
  absorbed: string[];
  /** The first problem found, to show beside the field. */
  error?: string;
  /** Anything that could not be read, as typed, to leave in the field. */
  rejected: string[];
}

/** One part of a paste that is an outward code, a space and an inward code or sector: "BS16 1AB", "BS16 1". */
const WITH_INWARD = /^[A-Z]{1,2}\d[A-Z\d]?\s+\d[A-Z]{0,2}$/;

/**
 * Adds what a fitter typed or pasted, which may be several codes separated by
 * commas, semicolons, new lines or spaces ("BS1, BS3 BA1"). A comma-separated
 * part that looks like a full postcode or sector ("BS16 1AB", "W1 2") is kept
 * whole, so it is refused as one rather than read as "BS16" plus nonsense, or
 * "W1 2" as the district W12.
 *
 * <p>Adding an area drops the districts in it that were already listed: the
 * area takes them in.
 */
export function addPostcodes(current: readonly string[], typed: string): AddPostcodesResult {
  const tokens = typed
    .toUpperCase()
    .split(/[,;\n\r]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .flatMap((part) => (WITH_INWARD.test(part) ? [part] : part.split(/\s+/)));
  let postcodes = [...current];
  const added: string[] = [];
  const skipped: string[] = [];
  const absorbed: string[] = [];
  const rejected: string[] = [];
  let error: string | undefined;
  for (const token of tokens) {
    const check = checkPostcode(token);
    if (!check.ok) {
      rejected.push(token);
      error ??= check.reason;
      continue;
    }
    if (postcodes.includes(check.code) || (check.kind === 'district' && isCovered(postcodes, check.code))) {
      skipped.push(check.code);
      continue;
    }
    if (check.kind === 'area') {
      const inside = postcodes.filter((p) => p !== check.code && covers(check.code, p));
      for (const code of inside) {
        const index = added.indexOf(code);
        if (index >= 0) added.splice(index, 1);
        else absorbed.push(code);
      }
      postcodes = postcodes.filter((p) => !inside.includes(p));
    }
    postcodes.push(check.code);
    added.push(check.code);
  }
  return { postcodes, added, skipped, absorbed, rejected, ...(error ? { error } : {}) };
}

/** Coverage grouped by area, for the coverage summary: whole areas first, then districts. */
export interface CoverageArea {
  area: string;
  name: string;
  whole: boolean;
  districts: string[];
}

export function coverageByArea(postcodes: readonly string[]): CoverageArea[] {
  const byArea = new Map<string, CoverageArea>();
  for (const code of postcodes) {
    const area = postcodeArea(code);
    const entry = byArea.get(area) ?? { area, name: areaName(area), whole: false, districts: [] };
    if (code === area) entry.whole = true;
    else entry.districts.push(code);
    byArea.set(area, entry);
  }
  const districtOrder = (a: string, b: string) => a.localeCompare(b, 'en-GB', { numeric: true });
  return [...byArea.values()]
    .map((entry) => ({ ...entry, districts: entry.districts.sort(districtOrder) }))
    .sort((a, b) => a.area.localeCompare(b.area));
}

// ---------------------------------------------------------------------------
// Trades, coverage, jobs
// ---------------------------------------------------------------------------

/**
 * A trade id: the backend's `InstallerTrade` name (`KITCHEN_FITTING`). Staff
 * approve a fitter's trades against their registrations, so a fitter cannot
 * change them; blocks may relabel them through their `trades` prop.
 */
export type FitterTrade = string;

export interface FitterTradeOption {
  id: FitterTrade;
  label: string;
}

/** Every trade the backend knows, with its name here. */
export const FITTER_TRADE_LABELS: Readonly<Record<string, string>> = {
  KITCHEN_FITTING: 'Kitchen fitting',
  JOINERY: 'Joinery',
  ELECTRICAL: 'Electrical',
  PLUMBING: 'Plumbing',
  GAS: 'Gas',
  TILING: 'Tiling',
  PLASTERING: 'Plastering',
  SOLAR_PV: 'Solar PV',
  BATTERY_STORAGE: 'Battery storage',
  EV_CHARGERS: 'EV chargers',
  OTHER: 'Other',
};

/** The trades a kitchen job can need: the board's filter when a block authors none. */
export const DEFAULT_FITTER_TRADES: readonly FitterTradeOption[] = [
  'KITCHEN_FITTING',
  'JOINERY',
  'PLUMBING',
  'ELECTRICAL',
  'GAS',
  'TILING',
  'PLASTERING',
].map((id) => ({ id, label: FITTER_TRADE_LABELS[id]! }));

/** A trade's name: the block's own label, the known name, or the id made readable. */
export function tradeLabel(id: FitterTrade, options: readonly FitterTradeOption[] = []): string {
  return (
    options.find((option) => option.id === id)?.label ??
    FITTER_TRADE_LABELS[id] ??
    id.toLowerCase().replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  );
}

export interface FitterCoverage {
  /** Canonical areas and districts ("BS", "BA1"). */
  postcodes: string[];
  /** Read-only: set by the shop when it approves the fitter. */
  trades: FitterTrade[];
}

/** Where a fitter stands with the shop. `APPLIED`/`REJECTED` may be an application with no fitter account yet. */
export type FitterState = 'APPLIED' | 'ACTIVE' | 'SUSPENDED' | 'REJECTED';

/**
 * Why a fitter cannot quote: the backend's blocker names, and `CERTIFICATES_OUTSTANDING` for an
 * applicant whose application still waits for a certificate. Unknown names are shown generically.
 */
export type FitterBlocker =
  | 'AGREEMENT_OUT_OF_DATE'
  | 'NOT_INSURED'
  | 'SUSPENDED'
  | 'CERTIFICATES_OUTSTANDING'
  | (string & {});

/** The signed-in fitter: their coverage, their standing, and what stops them quoting. */
export interface FitterAccount extends FitterCoverage {
  state: FitterState;
  businessName: string;
  canQuote: boolean;
  blockers: FitterBlocker[];
  /** A fresh signed `/installer-documents?token=` path, or null. */
  documentsLink: string | null;
}

/** Whether a fitter's areas can be changed: only once there is a fitter account. */
export function canEditAreas(account: Pick<FitterAccount, 'state'>): boolean {
  return account.state === 'ACTIVE' || account.state === 'SUSPENDED';
}

export interface TradeBidInput {
  /** Pounds, inc. VAT, for the fitting work only. */
  price: number;
  /** ISO date, `YYYY-MM-DD`. */
  availableFrom: string;
  note: string;
}

/** A quote's standing. Sending again after `withdrawn` makes it `sent` again. */
export type TradeBidStatus = 'sent' | 'withdrawn' | 'accepted' | 'declined';

export interface TradeBid extends TradeBidInput {
  id: string;
  jobId: string;
  status: TradeBidStatus;
  sentAt: string;
}

/** `awarded`: the shop chose a quote; `closed`: no more quotes taken; `cancelled`: the job is off. */
export type TradeJobStatus = 'open' | 'awarded' | 'closed' | 'cancelled';

export interface TradeJob {
  id: string;
  /** The shop's reference, e.g. "JOB-00012", for when the fitter calls about it. */
  reference?: string;
  title: string;
  /** Outward code only, e.g. "BS16". Never a full postcode. */
  outward: string;
  /** The area's name, e.g. "Bristol". */
  areaLabel: string;
  /** What the work is, one line each: "Fit 14 base and wall units". */
  scope: string[];
  /** The trades the job needs, for the board's filter. */
  trades: FitterTrade[];
  /** The kitchen itself: "Shaker, Sage Green, 14 units, laminate worktops". */
  kitchenSummary: string;
  /** ISO date the room is ready from, `YYYY-MM-DD`. */
  readyFrom: string;
  /** A guide the shop recorded. Never invented. */
  budgetGuide?: string;
  /** ISO timestamp after which the job takes no quotes. */
  closesAt?: string;
  /** The shop's notes about the pack (plan, parts list, access). */
  packNotes?: string;
  /** ISO timestamp. */
  postedAt: string;
  /** Quotes received so far (sent or accepted), including this fitter's own. */
  bids: number;
  status: TradeJobStatus;
  /** The full site postcode: only when this fitter's quote was accepted. */
  sitePostcode?: string;
  /** This fitter's own quote, once sent. */
  myBid?: TradeBid;
}

/** Whether a job still takes quotes at `now`. */
export function takesQuotes(job: Pick<TradeJob, 'status' | 'closesAt'>, now: Date = new Date()): boolean {
  if (job.status !== 'open') return false;
  if (!job.closesAt) return true;
  const closes = new Date(job.closesAt);
  return Number.isNaN(closes.getTime()) || closes.getTime() > now.getTime();
}

/** Fired on `window` after coverage is saved, so a job board on the same page reloads. */
export const COVERAGE_SAVED_EVENT = 'sk-fitter-coverage-saved';

/** Thrown when the signed-in shopper is not a fitter and has not applied to be one. */
export class NotAFitter extends Error {
  constructor() {
    super('You do not have a fitter account yet.');
    this.name = 'NotAFitter';
  }
}

/**
 * The fitter's side of fitting jobs. Every call may throw `SignInRequired`
 * (from `savedDesigns.ts`) when nobody is signed in, and {@link NotAFitter}
 * when the shopper is signed in but is not a fitter.
 */
export interface TradeJobs {
  getAccount(): Promise<FitterAccount>;
  /** Replaces the fitter's areas; trades cannot be changed here. */
  saveAreas(postcodes: string[]): Promise<FitterAccount>;
  /** The jobs this fitter may see: open ones in their areas and trades, and every job they quoted on. Newest first. */
  jobs(): Promise<TradeJob[]>;
  /** Sends a quote, or replaces this fitter's earlier one. */
  placeBid(jobId: string, bid: TradeBidInput): Promise<TradeJob>;
  withdrawBid(jobId: string): Promise<TradeJob>;
}

// ---------------------------------------------------------------------------
// Validation of a quote
// ---------------------------------------------------------------------------

export const BID_NOTE_MAX = 500;
export const BID_PRICE_MAX = 100_000;

export type BidErrors = Partial<Record<keyof TradeBidInput, string>>;

/** Today's date as `YYYY-MM-DD` in the fitter's own time zone. */
export function isoToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** What is wrong with a quote, field by field; an empty object means it can be sent. */
export function validateBid(bid: { price: string | number; availableFrom: string; note: string }, today: string = isoToday()): BidErrors {
  const errors: BidErrors = {};
  const priceText = String(bid.price).trim().replace(/^£/, '').replace(/,/g, '');
  const price = Number(priceText);
  if (!priceText) errors.price = 'Enter your price.';
  else if (!/^\d+(\.\d{1,2})?$/.test(priceText) || !Number.isFinite(price)) errors.price = 'Enter the price in pounds, e.g. 1450 or 1450.50.';
  else if (price <= 0) errors.price = 'The price must be more than £0.';
  else if (price > BID_PRICE_MAX) errors.price = `The price must be no more than ${formatPounds(BID_PRICE_MAX)}.`;
  if (!bid.availableFrom) errors.availableFrom = 'Choose the first day you can start.';
  else if (!isIsoDate(bid.availableFrom)) errors.availableFrom = 'Choose a date from the calendar.';
  else if (bid.availableFrom < today) errors.availableFrom = 'Choose today or a later date.';
  if (bid.note.length > BID_NOTE_MAX) errors.note = `Keep the note to ${BID_NOTE_MAX} characters.`;
  return errors;
}

export function formatPounds(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** An ISO date or timestamp as "12 Oct 2026". Date-only values are read as calendar days, not UTC midnights. */
export function formatJobDate(iso: string): string {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
}
